import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { ShieldAlert, Users, Building2, ClipboardList, CheckCircle2, XCircle, Pencil, Trash2, Plus, Lock, ShieldCheck, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

export default function AdminControl() {
  const { user } = useAuth();
  if (user && user.role !== "s_tier") return <Navigate to="/app" replace />;
  return (
    <div className="space-y-6" data-testid="admin-control-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">S-Tier admin control</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Workspace, accounts & approvals.</h1>
        <p className="text-sm text-[#555568] mt-2">
          The control plane for WYLO operations. All actions are recorded in the audit log.
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-white border border-[#E8ECF2]">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="workspaces" data-testid="admin-tab-workspaces">Workspaces</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="admin-tab-approvals">Hierarchy approvals</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="admin-tab-verifications">Domain verifications</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewPanel /></TabsContent>
        <TabsContent value="users"><UsersPanel /></TabsContent>
        <TabsContent value="workspaces"><WorkspacesPanel /></TabsContent>
        <TabsContent value="approvals"><ApprovalsPanel /></TabsContent>
        <TabsContent value="verifications"><VerificationsPanel /></TabsContent>
        <TabsContent value="audit"><AuditPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="text-[#555568] py-6">Loading…</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      <Stat icon={Users} label="Total users" value={data.users_total} />
      <Stat icon={Building2} label="Workspaces" value={data.workspaces_total} />
      <Stat icon={ClipboardList} label="Pending hierarchy requests" value={data.pending_hierarchy_requests} tone={data.pending_hierarchy_requests > 0 ? "warn" : "ok"} />
      <Stat icon={ShieldAlert} label="Suspended / pending review" value={data.suspended_accounts + data.pending_review_accounts} tone={data.suspended_accounts + data.pending_review_accounts > 0 ? "warn" : "ok"} />

      <section className="md:col-span-2 rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h3 className="font-display text-lg font-bold text-[#1A1A2E] mb-3">By tier</h3>
        <div className="space-y-2">
          {Object.entries(data.by_tier).map(([k, v]) => (
            <Row key={k} label={k} value={v} max={data.users_total} />
          ))}
        </div>
      </section>
      <section className="md:col-span-2 rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h3 className="font-display text-lg font-bold text-[#1A1A2E] mb-3">By role</h3>
        <div className="space-y-2">
          {Object.entries(data.by_role).map(([k, v]) => (
            <Row key={k} label={k} value={v} max={data.users_total} />
          ))}
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, max }) {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-[#555568]">
        <span className="capitalize">{label}</span>
        <span className="font-mono text-[#1A1A2E]">{value}</span>
      </div>
      <div className="mt-1 h-1.5 rounded bg-[#E8ECF2] overflow-hidden">
        <div className="h-full bg-[#2C6BAC]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-[#E8ECF2] bg-white p-5">
      <Icon size={16} className={tone === "warn" ? "text-[#BA7517]" : "text-[#2C6BAC]"} />
      <p className="font-display text-3xl font-bold text-[#1A1A2E] mt-3 tabular-nums">{value}</p>
      <p className="text-xs text-[#555568] mt-1">{label}</p>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = async () => {
    const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}`);
    setUsers(data.users || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="mt-6 space-y-4">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email…" data-testid="admin-users-search" />
        <Button onClick={load} className="bg-[#1A3A5C] text-white">Search</Button>
      </div>

      <div className="rounded-xl border border-[#E8ECF2] bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-[#F8F9FC] text-[#555568]">
            <tr>
              <th className="text-left p-3 font-medium">Email</th>
              <th className="text-left p-3 font-medium">Role</th>
              <th className="text-left p-3 font-medium">Tier</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="text-left p-3 font-medium">Workspace</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#E8ECF2]">
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3 text-xs uppercase tracking-wider">{u.role}</td>
                <td className="p-3 text-xs uppercase tracking-wider">{u.tier}</td>
                <td className="p-3"><StatusPill status={u.status} /></td>
                <td className="p-3 text-xs text-[#555568]">{u.workspace_id || "-"}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEdit(u)} data-testid={`admin-edit-${u.email}`}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" className="text-[#E24B4A]" onClick={async () => {
                    if (!window.confirm(`Permanently delete ${u.email}?`)) return;
                    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deleted."); load(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
                  }}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#555568] text-sm">No users.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && <EditUserDialog user={edit} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ role: user.role, tier: user.tier, status: user.status, org_tier: user.org_tier || "Frontline", workspace_id: user.workspace_id || "" });
  const save = async () => {
    try {
      await api.patch(`/admin/users/${user.id}`, form);
      toast.success("User updated.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {user.email}</DialogTitle>
          <DialogDescription>S-Tier actions are recorded in the audit log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <FieldSelect label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={["user","admin","super_admin","consultant","s_tier"]} />
          <FieldSelect label="Tier" value={form.tier} onChange={(v) => setForm({ ...form, tier: v })} options={["spark","focus-web","focus","sync-web","sync","momentum","command"]} />
          <FieldSelect label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={["active","suspended","pending_review"]} />
          <FieldSelect label="Org tier" value={form.org_tier} onChange={(v) => setForm({ ...form, org_tier: v })} options={["Executive Leadership","Senior Manager","Manager","Supervisor","Frontline","Consultant"]} />
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Workspace ID</label>
            <Input value={form.workspace_id} onChange={(e) => setForm({ ...form, workspace_id: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="admin-user-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatusPill({ status }) {
  const map = { active: "#1D9E75", suspended: "#E24B4A", pending_review: "#BA7517" };
  return (
    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: map[status] || "#555568" }}>
      {status}
    </span>
  );
}

function ApprovalsPanel() {
  const [requests, setRequests] = useState([]);
  const [decisionFor, setDecisionFor] = useState(null);
  const [rank, setRank] = useState(1);
  const [note, setNote] = useState("");
  const load = async () => {
    const { data } = await api.get("/hierarchy/requests");
    setRequests(data.requests || []);
  };
  useEffect(() => { load(); }, []);

  const approve = async (req) => {
    try {
      const res = await api.post(`/hierarchy/requests/${req.id}/approve`, { approved_rank: rank, note });
      toast.success(`Approved. ${res.data.affected_users} user${res.data.affected_users === 1 ? "" : "s"} updated.`);
      setDecisionFor(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    }
  };
  const reject = async (req) => {
    try {
      await api.post(`/hierarchy/requests/${req.id}/reject`, { note });
      toast.success("Rejected.");
      setDecisionFor(null);
      setNote("");
      load();
    } catch (e) {
      toast.error("Failed.");
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-[#E8ECF2] bg-white" data-testid="admin-approvals-list">
      {requests.length === 0 && <p className="p-6 text-sm text-[#555568]">No pending requests. 🌱</p>}
      <ul className="divide-y divide-[#E8ECF2]">
        {requests.map((r) => (
          <li key={r.id} className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display font-bold text-[#1A1A2E]">{r.name} <span className="text-xs uppercase tracking-wider text-[#555568] font-normal ml-1">{r.kind} · {r.action}</span></p>
                <p className="text-xs text-[#555568] mt-1">
                  Requested by <span className="font-mono">{r.user_email}</span> · tier {r.tier} · workspace {r.workspace_id || "(individual)"}
                </p>
                <p className="text-xs text-[#555568] font-mono">{new Date(r.created_at).toLocaleString()}</p>
                {r.category && <p className="text-xs text-[#555568] mt-1">Category: {r.category}</p>}
                {r.suggested_rank && <p className="text-xs text-[#555568]">Suggested rank: {r.suggested_rank}</p>}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-[#1D9E75] hover:bg-[#168862] text-white"
                  onClick={() => { setDecisionFor({ ...r, mode: "approve" }); setRank(r.suggested_rank || 1); }}
                  data-testid={`admin-approve-${r.id}`}
                >
                  <CheckCircle2 size={14} className="mr-1" /> Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#E24B4A] text-[#E24B4A] hover:bg-[#E24B4A]/10"
                  onClick={() => { setDecisionFor({ ...r, mode: "reject" }); }}
                  data-testid={`admin-reject-${r.id}`}
                >
                  <XCircle size={14} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {decisionFor && (
        <Dialog open onOpenChange={() => setDecisionFor(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {decisionFor.mode === "approve" ? "Approve request" : "Reject request"}
              </DialogTitle>
              <DialogDescription>
                {decisionFor.mode === "approve"
                  ? "Set the final rank - this is applied to every user in the requester's workspace (or just the requester if they're solo)."
                  : "Optional note. This is recorded in the audit log."}
              </DialogDescription>
            </DialogHeader>
            {decisionFor.mode === "approve" && (
              <div className="space-y-3 mt-2">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Final rank within {decisionFor.kind}s</label>
                  <Input type="number" min={1} value={rank} onChange={(e) => setRank(parseInt(e.target.value || "1", 10))} />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Note (optional)</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setDecisionFor(null)}>Cancel</Button>
              <Button
                className={decisionFor.mode === "approve" ? "bg-[#1D9E75] hover:bg-[#168862] text-white" : "bg-[#E24B4A] hover:bg-[#c03d3c] text-white"}
                onClick={() => decisionFor.mode === "approve" ? approve(decisionFor) : reject(decisionFor)}
                data-testid="admin-decision-confirm"
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function WorkspacesPanel() {
  const [workspaces, setWorkspaces] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/workspaces?q=${encodeURIComponent(q)}`);
    setWorkspaces(data.workspaces || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="mt-6 space-y-4" data-testid="admin-workspaces-panel">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search workspaces…" data-testid="admin-workspaces-search" />
        <Button onClick={load} className="bg-[#1A3A5C] text-white">Search</Button>
        <Button onClick={() => setCreating(true)} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="admin-workspaces-new">
          <Plus size={14} className="mr-1" /> New workspace
        </Button>
      </div>

      <div className="rounded-xl border border-[#E8ECF2] bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-workspaces-table">
          <thead className="bg-[#F8F9FC] text-[#555568]">
            <tr>
              <th className="text-left p-3 font-medium">ID</th>
              <th className="text-left p-3 font-medium">Name</th>
              <th className="text-left p-3 font-medium">Domain</th>
              <th className="text-left p-3 font-medium">Users</th>
              <th className="text-left p-3 font-medium">Tiers</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {workspaces.map((w) => (
              <tr key={w.id} className="border-t border-[#E8ECF2]">
                <td className="p-3 font-mono text-xs flex items-center gap-2">
                  {w.protected && <Lock size={12} className="text-[#BA7517]" />}
                  {w.id}
                </td>
                <td className="p-3 text-xs">{w.name}</td>
                <td className="p-3 font-mono text-xs text-[#555568]">{w.domain || "-"}</td>
                <td className="p-3 text-xs font-mono">{w.users_count}</td>
                <td className="p-3 text-xs text-[#555568]">{(w.tiers || []).slice(0, 3).join(", ") || "-"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setViewing(w)} data-testid={`admin-ws-view-${w.id}`}>View</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(w)} data-testid={`admin-ws-edit-${w.id}`}><Pencil size={14} /></Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={w.protected}
                    onClick={() => setRenaming(w)}
                    data-testid={`admin-ws-rename-${w.id}`}
                    title={w.protected ? "Protected workspace" : "Rename"}
                  >
                    Rename
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={w.protected}
                    className="text-[#E24B4A]"
                    onClick={() => setDeleting(w)}
                    data-testid={`admin-ws-delete-${w.id}`}
                    title={w.protected ? "Protected workspace" : "Delete"}
                  >
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {workspaces.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-[#555568] text-sm">No workspaces.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && <CreateWorkspaceDialog onClose={() => setCreating(false)} onSaved={load} />}
      {editing && <EditWorkspaceDialog ws={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {renaming && <RenameWorkspaceDialog ws={renaming} onClose={() => setRenaming(null)} onSaved={load} />}
      {deleting && <DeleteWorkspaceDialog ws={deleting} onClose={() => setDeleting(null)} onSaved={load} />}
      {viewing && <ViewWorkspaceDialog ws={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function CreateWorkspaceDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ id: "", name: "", domain: "", notes: "" });
  const save = async () => {
    try {
      await api.post("/admin/workspaces", form);
      toast.success("Workspace created.");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>Create a new workspace. Assign users to it from the Users tab.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <FieldText label="ID (slug)" value={form.id} onChange={(v) => setForm({ ...form, id: v })} placeholder="e.g. acme-corp" testid="admin-ws-create-id" />
          <FieldText label="Display name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Acme Corp" />
          <FieldText label="Domain" value={form.domain} onChange={(v) => setForm({ ...form, domain: v })} placeholder="acme.com" />
          <FieldText label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} placeholder="Optional" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="admin-ws-create-save">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditWorkspaceDialog({ ws, onClose, onSaved }) {
  const [form, setForm] = useState({ name: ws.name || "", domain: ws.domain || "", notes: ws.notes || "" });
  const save = async () => {
    try {
      await api.patch(`/admin/workspaces/${encodeURIComponent(ws.id)}`, form);
      toast.success("Workspace updated.");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {ws.id}</DialogTitle>
          <DialogDescription>Update display name, domain, or notes. Use Rename to change the workspace ID.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <FieldText label="Display name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-ws-edit-name" />
          <FieldText label="Domain" value={form.domain} onChange={(v) => setForm({ ...form, domain: v })} />
          <FieldText label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="admin-ws-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameWorkspaceDialog({ ws, onClose, onSaved }) {
  const [newId, setNewId] = useState("");
  const save = async () => {
    try {
      const { data } = await api.post(`/admin/workspaces/${encodeURIComponent(ws.id)}/rename`, { new_id: newId });
      toast.success(`Renamed. ${data.users_moved} user${data.users_moved === 1 ? "" : "s"} moved.`);
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename workspace</DialogTitle>
          <DialogDescription>
            Renaming <span className="font-mono">{ws.id}</span> will update every user, hierarchy request, and metadata record assigned to it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <FieldText label="New ID" value={newId} onChange={setNewId} placeholder="new-slug" testid="admin-ws-rename-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="admin-ws-rename-save" disabled={!newId.trim()}>Rename</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteWorkspaceDialog({ ws, onClose, onSaved }) {
  const [cascade, setCascade] = useState(false);
  const [confirm, setConfirm] = useState("");
  const ok = confirm === ws.id;
  const save = async () => {
    try {
      const { data } = await api.delete(`/admin/workspaces/${encodeURIComponent(ws.id)}?cascade=${cascade}`);
      toast.success(cascade ? `Workspace and ${data.users_deleted} user${data.users_deleted === 1 ? "" : "s"} deleted.` : "Workspace deleted. Users detached.");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete workspace {ws.id}?</DialogTitle>
          <DialogDescription>
            This will remove the workspace record and {cascade ? "delete every user inside it" : "detach all users (their accounts are kept; workspace_id set to null)"}.
            This is irreversible.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={cascade}
              onChange={(e) => setCascade(e.target.checked)}
              data-testid="admin-ws-delete-cascade"
            />
            Also delete the {ws.users_count} user{ws.users_count === 1 ? "" : "s"} in this workspace (cascade).
          </label>
          <FieldText
            label={`Type "${ws.id}" to confirm`}
            value={confirm}
            onChange={setConfirm}
            testid="admin-ws-delete-confirm-text"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={!ok}
            className="bg-[#E24B4A] hover:bg-[#c03d3c] text-white"
            data-testid="admin-ws-delete-confirm"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewWorkspaceDialog({ ws, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/admin/workspaces/${encodeURIComponent(ws.id)}`).then(({ data }) => setDetail(data));
  }, [ws.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{ws.name} <span className="font-mono text-xs text-[#555568]">({ws.id})</span></DialogTitle>
          <DialogDescription>
            {detail ? `${detail.users_count} member${detail.users_count === 1 ? "" : "s"}.` : "Loading…"}
            {ws.domain ? ` Domain: ${ws.domain}.` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto">
          {detail && detail.users?.length > 0 && (
            <table className="w-full text-xs">
              <thead className="bg-[#F8F9FC] text-[#555568]">
                <tr>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Role</th>
                  <th className="text-left p-2">Tier</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {detail.users.map((u) => (
                  <tr key={u.id} className="border-t border-[#E8ECF2]">
                    <td className="p-2 font-mono">{u.email}</td>
                    <td className="p-2 uppercase tracking-wider">{u.role}</td>
                    <td className="p-2 uppercase tracking-wider">{u.tier}</td>
                    <td className="p-2"><StatusPill status={u.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {detail && detail.users?.length === 0 && (
            <p className="text-sm text-[#555568] py-4">No users assigned to this workspace.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldText({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1"
      />
    </div>
  );
}

function VerificationsPanel() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [decisionFor, setDecisionFor] = useState(null);
  const [docFor, setDocFor] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/workspace-verifications?status=${statusFilter}`);
    setItems(data.verifications || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async (verb) => {
    if (!decisionFor) return;
    try {
      await api.post(`/admin/workspace-verifications/${decisionFor.workspace_id}/${verb}`, { note });
      toast.success(`${verb === "approve" ? "Approved" : "Rejected"}.`);
      setDecisionFor(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="admin-verifications-panel">
      <div className="flex gap-2 items-center">
        <span className="text-xs uppercase tracking-[0.18em] text-[#555568] font-semibold">Filter</span>
        {["pending", "approved", "rejected"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? "default" : "outline"}
            className={statusFilter === s ? "bg-[#1A3A5C] text-white" : ""}
            onClick={() => setStatusFilter(s)}
            data-testid={`admin-verify-filter-${s}`}
          >
            {s}
          </Button>
        ))}
      </div>

      <div className="rounded-xl border border-[#E8ECF2] bg-white">
        {items.length === 0 && <p className="p-6 text-sm text-[#555568]">No {statusFilter} verifications.</p>}
        <ul className="divide-y divide-[#E8ECF2]">
          {items.map((v) => {
            const ver = v.verification || {};
            return (
              <li key={v.workspace_id} className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display font-bold text-[#1A1A2E]">
                      {v.name} <span className="text-xs uppercase tracking-wider text-[#555568] font-normal ml-1">{v.workspace_id}</span>
                    </p>
                    <p className="text-xs text-[#555568] mt-1">
                      {ver.declarant_name} · {ver.role_title} · {ver.declarant_email}
                    </p>
                    <p className="text-xs text-[#555568]">
                      Type: <span className="uppercase">{ver.kind || "-"}</span>
                      {ver.declared_business_name ? ` · Business: ${ver.declared_business_name}` : ""}
                    </p>
                    {ver.notes && <p className="text-xs text-[#555568] mt-1">Note: {ver.notes}</p>}
                    <p className="text-xs text-[#555568] font-mono mt-1">
                      Declared {ver.declared_at ? new Date(ver.declared_at).toLocaleString() : "-"}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {v.has_doc && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDocFor(v)}
                        data-testid={`admin-verify-view-doc-${v.workspace_id}`}
                      >
                        <FileText size={12} className="mr-1" /> View document
                      </Button>
                    )}
                    {statusFilter === "pending" && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-[#1D9E75] hover:bg-[#168862] text-white"
                          onClick={() => setDecisionFor({ ...v, mode: "approve" })}
                          data-testid={`admin-verify-approve-${v.workspace_id}`}
                        >
                          <CheckCircle2 size={14} className="mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#E24B4A] text-[#E24B4A] hover:bg-[#E24B4A]/10"
                          onClick={() => setDecisionFor({ ...v, mode: "reject" })}
                          data-testid={`admin-verify-reject-${v.workspace_id}`}
                        >
                          <XCircle size={14} className="mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {decisionFor && (
        <Dialog open onOpenChange={() => setDecisionFor(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{decisionFor.mode === "approve" ? "Approve verification" : "Reject verification"}</DialogTitle>
              <DialogDescription>
                {decisionFor.mode === "approve"
                  ? `Mark ${decisionFor.workspace_id} as a verified business workspace.`
                  : `Reject the submission. Optionally include a reason - it will be visible to the workspace admin.`}
              </DialogDescription>
            </DialogHeader>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-[#555568]">Note (optional)</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-verify-decision-note" />
            </div>
            <DialogFooter className="mt-3">
              <Button variant="outline" onClick={() => setDecisionFor(null)}>Cancel</Button>
              <Button
                className={decisionFor.mode === "approve" ? "bg-[#1D9E75] hover:bg-[#168862] text-white" : "bg-[#E24B4A] hover:bg-[#c03d3c] text-white"}
                onClick={() => decide(decisionFor.mode)}
                data-testid="admin-verify-decision-confirm"
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {docFor && <ViewDocDialog ws={docFor} onClose={() => setDocFor(null)} />}
    </div>
  );
}

function ViewDocDialog({ ws, onClose }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    api.get(`/admin/workspace-verifications/${ws.workspace_id}/document`)
      .then(({ data }) => setDoc(data))
      .catch((e) => toast.error(e.response?.data?.detail || "No document."));
  }, [ws.workspace_id]);
  const isImage = doc?.mime?.startsWith("image/");
  const isPdf = doc?.mime === "application/pdf";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#1A3A5C]" />
            {doc?.filename || "Verification document"}
          </DialogTitle>
          <DialogDescription>
            {doc ? `${doc.mime} · ${(doc.bytes / 1024).toFixed(1)} KB` : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          {doc && isImage && <img src={doc.data_uri} alt="Verification doc" className="max-w-full rounded-md border border-[#E8ECF2]" />}
          {doc && isPdf && (
            <iframe src={doc.data_uri} title="Verification PDF" className="w-full h-[60vh] border border-[#E8ECF2] rounded-md" />
          )}
          {doc && !isImage && !isPdf && (
            <a href={doc.data_uri} download={doc.filename} className="text-sm text-[#2C6BAC] underline">Download {doc.filename}</a>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditPanel() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/admin/audit?limit=100").then(({ data }) => setEvents(data.events || [])); }, []);

  const exportLog = async (format) => {
    try {
      const resp = await api.get(`/admin/audit/export?format=${format}&limit=5000`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: format === "csv" ? "text/csv" : "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url;
      a.download = `wylo-audit-${ts}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Audit log exported as ${format.toUpperCase()}.`);
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-[#E8ECF2] bg-white p-6" data-testid="admin-audit-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold text-[#1A1A2E]">Recent S-Tier actions</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => exportLog("csv")} data-testid="admin-audit-export-csv">
            <Download size={12} className="mr-1.5" /> Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportLog("json")} data-testid="admin-audit-export-json">
            <Download size={12} className="mr-1.5" /> Export JSON
          </Button>
        </div>
      </div>
      {events.length === 0 && <p className="text-sm text-[#555568]">No events yet.</p>}
      <ul className="text-xs space-y-2">
        {events.map((e, i) => (
          <li key={i} className="border-t border-[#E8ECF2] pt-2">
            <span className="font-mono text-[#555568]">{new Date(e.at).toLocaleString()}</span>
            <span className="mx-2 text-[#1A1A2E] font-semibold">{e.action}</span>
            <span className="text-[#555568]">{e.actor_email} → {e.target_email || e.target_user_id || e.target_workspace_id || ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
